// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

import { resolveId } from '@medplum/core';
import type { Appointment, Composition, CoverageEligibilityResponse, Organization, Patient } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import type { PatientSystemStatus } from '@polarissleep/shared';
import { getPatientSystemStatus, PATIENT_SYSTEM_STATUS } from '@polarissleep/shared';
import { useCallback, useEffect, useState } from 'react';
import { getPayorId } from '../utils/getPayorId';
import { patientDisplayName, patientIdFromCoverageSubject } from '../utils/patient-display';

export interface IneligibleInsuranceEntry {
  cerRef: string;
  patientId: string;
  patientName: string;
  dateChecked: string;
  status: string;
  errorCode: string;
  requestedSTC: string;
  payorId: string;
  nextEncounterDate: string | undefined;
  nextEncounterId: string | undefined;
  compositionId: string | undefined;
}

export interface UseIneligibleInsuranceReturn {
  entries: IneligibleInsuranceEntry[];
  loading: boolean;
  total: number;
}

const STATUS_LABELS: Record<PatientSystemStatus, string> = {
  [PATIENT_SYSTEM_STATUS.active]: 'Active Patient',
  [PATIENT_SYSTEM_STATUS.inactive]: 'Inactive',
  [PATIENT_SYSTEM_STATUS.prospect]: 'Prospect',
  [PATIENT_SYSTEM_STATUS.archived]: 'Archived',
};

function getStatusLabel(patient: Patient | undefined): string {
  if (!patient) {
    return '—';
  }
  return STATUS_LABELS[getPatientSystemStatus(patient)] ?? '—';
}

function getCERKey(cer: CoverageEligibilityResponse): string | undefined {
  return cer.id;
}

/** Batch `_id` queries need an explicit count or the server may page (~20). */
const BATCH_RESOURCE_SEARCH_COUNT = '1000';

export function useIneligibleInsurance({
  page,
  pageSize,
  search,
}: {
  page: number;
  pageSize: number;
  search: string;
}): UseIneligibleInsuranceReturn {
  const medplum = useMedplum();
  const [entries, setEntries] = useState<IneligibleInsuranceEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);

  const searchFilter = search.toLowerCase();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [errorCERs, completeCERs] = await Promise.all([
        medplum.searchResources('CoverageEligibilityResponse', {
          outcome: 'error',
          _sort: '-created',
          _count: '200',
        }),
        medplum.searchResources('CoverageEligibilityResponse', {
          outcome: 'complete',
          _sort: '-created',
          _count: '200',
        }),
      ]);

      const ineligibleComplete = completeCERs.filter((cer) => cer.insurance?.[0]?.inforce !== true);

      const seen = new Set<string>();
      const merged: CoverageEligibilityResponse[] = [];
      for (const cer of [...errorCERs, ...ineligibleComplete]) {
        const key = getCERKey(cer);
        if (key && !seen.has(key)) {
          seen.add(key);
          merged.push(cer);
        }
      }

      const patientIds = [...new Set(merged.map((cer) => patientIdFromCoverageSubject(cer.patient)).filter(Boolean))];
      const insurerIds = [...new Set(merged.map((cer) => resolveId(cer.insurer)).filter(Boolean) as string[])];

      const [patientResources, orgResources] = await Promise.all([
        patientIds.length > 0
          ? medplum.searchResources('Patient', {
              _id: patientIds.join(','),
              _count: BATCH_RESOURCE_SEARCH_COUNT,
            })
          : Promise.resolve([] as Patient[]),
        insurerIds.length > 0
          ? medplum.searchResources('Organization', {
              _id: insurerIds.join(','),
              _count: BATCH_RESOURCE_SEARCH_COUNT,
            })
          : Promise.resolve([] as Organization[]),
      ]);

      const patientMap: Record<string, Patient> = {};
      for (const p of patientResources) {
        if (p.id) {
          patientMap[`Patient/${p.id}`] = p;
        }
      }

      const orgMap: Record<string, Organization> = {};
      for (const o of orgResources) {
        if (o.id) {
          orgMap[`Organization/${o.id}`] = o;
        }
      }

      const enriched = await Promise.all(
        merged.map(async (cer) => {
          const patientId = patientIdFromCoverageSubject(cer.patient);
          const patient = patientId ? patientMap[`Patient/${patientId}`] : undefined;
          const patientName = patientDisplayName(patient, cer.patient);
          const oid = resolveId(cer.insurer);
          const org = oid ? orgMap[`Organization/${oid}`] : undefined;

          const [appointments, compositions] = await Promise.all([
            patientId
              ? medplum.searchResources('Appointment', {
                  patient: `Patient/${patientId}`,
                  status: 'booked',
                  _count: '1',
                  date: `ge${new Date().toISOString().split('T')[0]}`,
                })
              : Promise.resolve([] as Appointment[]),
            patientId
              ? medplum.searchResources('Composition', {
                  subject: `Patient/${patientId}`,
                  status: 'final',
                  _sort: '-_lastUpdated',
                  _count: '1',
                })
              : Promise.resolve([] as Composition[]),
          ]);

          const nextAppt = appointments[0];
          const composition = compositions[0];

          const payorId = getPayorId(org);

          return {
            cerRef: cer.id ?? '',
            patientId,
            patientName,
            dateChecked: cer.created ?? '',
            status: getStatusLabel(patient),
            errorCode: cer.error?.[0]?.code?.coding?.[0]?.display ?? cer.error?.[0]?.code?.text ?? '—',
            requestedSTC:
              cer.insurance?.[0]?.item?.[0]?.category?.coding?.[0]?.display ?? '30 - Health Benefit Plan Coverage',
            payorId,
            nextEncounterDate: nextAppt?.start,
            nextEncounterId: nextAppt?.id,
            compositionId: composition?.id,
          } satisfies IneligibleInsuranceEntry;
        })
      );

      const filtered = searchFilter
        ? enriched.filter(
            (e) =>
              e.patientName.toLowerCase().includes(searchFilter) ||
              e.payorId.toLowerCase().includes(searchFilter) ||
              e.errorCode.toLowerCase().includes(searchFilter)
          )
        : enriched;

      setTotal(filtered.length);
      setEntries(filtered.slice((page - 1) * pageSize, page * pageSize));
    } finally {
      setLoading(false);
    }
  }, [medplum, page, pageSize, searchFilter]);

  useEffect(() => {
    fetchData().catch(console.error);
  }, [fetchData]);

  return { entries, loading, total };
}
